// IM-XPR-001..003 — Adaptadores da nova experiência sobre as APIs EXISTENTES (nenhum motor novo). Cada função
// só lê; a tela decide a apresentação. Toda frase de consultor usa números reais do tenant (nunca do protótipo).
import {
  deriveMacroPhases, objectiveOf, OBJECTIVES, type BattlecardListItemDto, type CompetitorDetailDto, type CompetitorMonitoringDto, type JourneyCatalogItemDto, type JourneyDto, type MacroPhaseView,
  type ObjectiveDef, type ObjectiveKey, type ObjectiveStatus, type PlanningOverviewDto, type SourceDto, type StepId, MONTH_NAME, PLAN_STATUS_LABEL, TRACK_STATUS_LABEL,
} from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { battlecardApi } from '../data/battlecards';
import { competitorAdapter } from '../data/competitors';
import { intelApi } from '../data/intel';
import { journeysApi } from '../data/journeys';
import { planningApi } from '../data/planning';
import { sourceRecoApi } from '../data/sources';
import { consolidatePages, type CompetitorSuggestions, type PageRecommendation } from './pages';

/** Etapas em linguagem de consultor (o título técnico continua no detalhe da jornada). */
export const STEP_PLAIN: Partial<Record<StepId, string>> = {
  'J2-01': 'Informar os concorrentes que você já conhece', 'J2-02': 'Avaliar candidatos sugeridos pela análise de mercado', 'J2-03': 'Conferir a identidade de cada empresa',
  'J2-04': 'Classificar cada empresa', 'J2-05': 'Pesquisar sites e fontes dos concorrentes', 'J2-06': 'Montar o dossiê de cada concorrente',
  'J2-07': 'Comparar ofertas, mercados e posicionamento', 'J2-08': 'Revisar os pontos sensíveis do dossiê', 'J2-09': 'Escolher páginas para acompanhar',
  'J2-10': 'Ativar o acompanhamento das páginas', 'J2-11': 'Acompanhar redes sociais autorizadas', 'J2-12': 'Ver as mudanças detectadas', 'J2-13': 'Tratar os alertas',
  'J2-14': 'Propor uma resposta às mudanças importantes', 'J2-15': 'Registrar sua decisão', 'J2-16': 'Acompanhar continuamente',
  'J4-01': 'Reunir suas ofertas', 'J4-02': 'Ligar ofertas a perfis e segmentos', 'J4-03': 'Ligar ofertas aos concorrentes', 'J4-04': 'Ver lacunas e sobreposições',
  'J4-05': 'Reunir os preços disponíveis', 'J4-06': 'Avaliar sinais de demanda', 'J4-07': 'Avaliar a capacidade de entrega', 'J4-08': 'Priorizar as ofertas',
  'J4-09': 'Decidir o que fortalecer, combinar, reposicionar ou retirar', 'J4-10': 'Propor ofertas de entrada e pacotes', 'J4-11': 'Registrar a decisão', 'J4-12': 'Acompanhar os efeitos',
};
export const stepPlain = (id: StepId | null | undefined, fallback = '') => (id && STEP_PLAIN[id]) || fallback;
export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
export const countWord = (n: number, one: string, many: string) => (n === 1 ? `uma ${one}` : `${n} ${many}`);

/** O que uma jornada de objetivo expõe para a tela. */
export interface ObjectiveSummary {
  def: ObjectiveDef;
  status: ObjectiveStatus;
  /** Frase curta: próxima ação ou o que falta para começar. */
  next: string | null;
  catalog: JourneyCatalogItemDto | null;
  journey: JourneyDto | null;
  phases: MacroPhaseView[] | null;
}

/** Macrofases de uma instância viva, com a próxima ação do próprio motor. */
export function phasesOf(def: ObjectiveDef, d: JourneyDto): MacroPhaseView[] {
  return deriveMacroPhases(def.phases, d.steps, d.nextAction?.stepId ?? null);
}

const LIVE = new Set(['EM_ANDAMENTO', 'PAUSADA', 'EM_ACOMPANHAMENTO', 'CONCLUIDA']);
/** Catálogo das jornadas + instância viva de cada uma (leitura agregada do motor). Camada desligada = só conceito. */
export async function loadJourneyObjectives(journeysOn: boolean, signal?: AbortSignal, only?: ObjectiveKey): Promise<Partial<Record<ObjectiveKey, { catalog: JourneyCatalogItemDto | null; journey: JourneyDto | null }>>> {
  if (!journeysOn) return {};
  let items: JourneyCatalogItemDto[] = [];
  try { items = (await journeysApi.types(signal)).items; } catch (e) { if (e instanceof ApiError && e.status === 404) return {}; throw e; }
  const out: Partial<Record<ObjectiveKey, { catalog: JourneyCatalogItemDto | null; journey: JourneyDto | null }>> = {};
  await Promise.all(OBJECTIVES.filter((o) => o.mode === 'JORNADA' && (!only || o.key === only)).map(async (o) => {
    const catalog = items.find((x) => x.type === o.journeyType && x.available) ?? null;
    const live = catalog?.live.find((l) => LIVE.has(l.lifecycle)) ?? null;
    const journey = live ? await journeysApi.get(live.id, signal).catch(() => null) : null;
    out[o.key] = { catalog, journey };
  }));
  return out;
}

export interface TargetFacts { targets: number; targetsReview: number; targetsApproved: number; people: number; peopleReview: number }
export async function loadTargetFacts(signal?: AbortSignal): Promise<TargetFacts | null> {
  try {
    const p = await intelApi.pipelineSummary(signal);
    return { targets: p.targets ?? 0, targetsReview: p.targetsReview ?? 0, targetsApproved: p.targetsApproved ?? 0, people: p.people ?? 0, peopleReview: p.peopleReview ?? 0 };
  } catch { return null; }
}

/** IM-JRN-015/016: situação do planejamento (null = leitura indisponível; os objetivos continuam visíveis). */
export async function loadPlanningOverview(signal?: AbortSignal): Promise<PlanningOverviewDto | null> {
  try { return await planningApi.overview(signal); } catch { return null; }
}

/** Planejar e acompanhar: situação e próxima ação a partir da base, do plano e do realizado (dados reais). */
export function planObjectiveSummary(def: ObjectiveDef, p: PlanningOverviewDto | null): ObjectiveSummary {
  const base = { def, catalog: null, journey: null, phases: null };
  if (!p) return { ...base, status: 'COMECAR', next: null };
  if (def.key === 'planejar') {
    if (!p.plan) return { ...base, status: 'COMECAR', next: p.base.level === 'SEM_DADOS' ? 'Comece pela base: faturamento e despesas.' : `Base pronta. Crie o plano de ${p.planYear}.` };
    if (p.plan.status === 'APROVADO') return { ...base, status: 'PRONTO', next: `Plano ${p.plan.year} aprovado (versão ${p.plan.versionNo}).` };
    return { ...base, status: 'CONTINUAR', next: p.plan.status === 'EM_REVISAO' ? `Plano ${p.plan.year} aguardando aprovação.` : `Plano ${p.plan.year} em ${PLAN_STATUS_LABEL[p.plan.status ?? 'RASCUNHO'].toLowerCase()}: revise cenários e metas.` };
  }
  const t = p.tracking;
  if (!t) return { ...base, status: 'AGUARDANDO_PLANO', next: 'Começa quando houver um plano aprovado.' };
  if (t.dueMonth) return { ...base, status: 'CONTINUAR', next: `Registre o realizado de ${MONTH_NAME[t.dueMonth - 1]}.` };
  if (t.reviewDue && t.lastMonth) return { ...base, status: 'CONTINUAR', next: `${TRACK_STATUS_LABEL[t.status]}: revise ${MONTH_NAME[t.lastMonth - 1]}.` };
  return { ...base, status: 'PRONTO', next: `${TRACK_STATUS_LABEL[t.status]}${t.openActions ? ` · ${t.openActions} ${t.openActions === 1 ? 'ação aberta' : 'ações abertas'}` : ''}.` };
}

/** Situação e próxima ação de cada objetivo (linguagem natural; sem códigos). */
export function summarizeObjectives(j: Awaited<ReturnType<typeof loadJourneyObjectives>>, targets: TargetFacts | null, planning: PlanningOverviewDto | null = null): ObjectiveSummary[] {
  return OBJECTIVES.map((def) => {
    if (def.mode === 'PLANO') return planObjectiveSummary(def, planning);
    if (def.mode === 'CAPACIDADES') {
      const t = targets;
      const review = (t?.targetsReview ?? 0) + (t?.peopleReview ?? 0);
      const status: ObjectiveStatus = review > 0 || (t?.targets ?? 0) > 0 ? 'CONTINUAR' : 'COMECAR';
      const next = (t?.targetsReview ?? 0) > 0 ? `${plural(t!.targetsReview, 'empresa para avaliar', 'empresas para avaliar')}.`
        : (t?.peopleReview ?? 0) > 0 ? `${plural(t!.peopleReview, 'pessoa para conferir', 'pessoas para conferir')}.`
        : (t?.targets ?? 0) > 0 ? 'Encontre mais empresas ou localize as pessoas certas.' : 'Comece pelo perfil de cliente ideal.';
      return { def, status, next, catalog: null, journey: null, phases: null };
    }
    const x = j[def.key];
    const d = x?.journey ?? null;
    if (d) {
      const phases = phasesOf(def, d);
      const done = d.journey.lifecycle === 'CONCLUIDA' || d.journey.lifecycle === 'EM_ACOMPANHAMENTO';
      const current = phases.find((p) => p.current);
      const next = d.journey.lifecycle === 'PAUSADA' ? 'Pausado. Retome quando quiser.'
        : d.nextAction?.stepId ? `${current ? `${current.name}: ` : ''}${stepPlain(d.nextAction.stepId, d.nextAction.title)}.`
        : done ? 'Em acompanhamento contínuo.' : null;
      return { def, status: done ? 'PRONTO' : 'CONTINUAR', next, catalog: x!.catalog, journey: d, phases };
    }
    const c = x?.catalog ?? null;
    if (c?.state === 'PRONTA_PARA_COMECAR') return { def, status: 'COMECAR', next: 'Tudo pronto para começar.', catalog: c, journey: null, phases: null };
    if (c) return { def, status: 'AGUARDANDO_INFORMACAO', next: c.missing[0]?.label ? `Falta: ${c.missing[0].label.replace(/\.$/, '')}.` : c.needs, catalog: c, journey: null, phases: null };
    return { def, status: 'COMECAR', next: null, catalog: null, journey: null, phases: null };
  });
}

// ── Concorrentes ─────────────────────────────────────────────────────────────────────────────────────────────────
/** Concorrentes vivos (sem arquivados nem substituídos) e as contagens usadas nas frases e nas fases. */
export interface CompetitorFacts {
  items: CompetitorDetailDto[];
  toClassify: CompetitorDetailDto[];
  confirmed: number; hypotheses: number; related: number; discarded: number;
}
export const needsClassification = (k: Pick<CompetitorDetailDto, 'status' | 'reviewPending' | 'reviewedAt'>) => k.reviewPending || (k.status === 'HYPOTHESIS' && !k.reviewedAt);
export async function loadCompetitorFacts(signal?: AbortSignal): Promise<CompetitorFacts> {
  const all = (await competitorAdapter.list(signal)).items.filter((k) => k.status !== 'ARCHIVED' && k.status !== 'SUPERSEDED' && !k.archivedAt);
  return {
    items: all, toClassify: all.filter((k) => k.status !== 'DISCARDED' && needsClassification(k)),
    confirmed: all.filter((k) => k.status === 'CONFIRMED').length, hypotheses: all.filter((k) => k.status === 'HYPOTHESIS').length,
    related: all.filter((k) => k.status === 'RELATED').length, discarded: all.filter((k) => k.status === 'DISCARDED').length,
  };
}

/** Monitoramento de cada concorrente com battlecard ativo (fontes, sugestões, mudanças e sinais) — leitura do módulo dono. */
export interface CompetitorWatch { competitorId: string; competitorName: string; battlecardId: string; monitoring: CompetitorMonitoringDto }
export async function loadCompetitorWatch(signal?: AbortSignal): Promise<{ cards: BattlecardListItemDto[]; watch: CompetitorWatch[] }> {
  const cards = await battlecardApi.list(signal).then((x) => x.items).catch(() => [] as BattlecardListItemDto[]);
  const live = cards.filter((x) => x.battlecard && x.battlecard.status === 'ACTIVE').slice(0, 8);
  const read = await Promise.all(live.map((x) => battlecardApi.monitoring(x.battlecard!.id, signal)
    .then((m): CompetitorWatch => ({ competitorId: x.competitor.id, competitorName: x.competitor.name, battlecardId: x.battlecard!.id, monitoring: m })).catch(() => null)));
  return { cards, watch: read.filter((x): x is CompetitorWatch => x !== null) };
}

/** Páginas recomendadas consolidadas (fontes recomendadas + sugestões do battlecard), sem as já monitoradas. */
export interface PagesData { all: PageRecommendation[]; monitored: SourceDto[] }
export async function loadPageRecommendations(signal?: AbortSignal, pre?: CompetitorWatch[]): Promise<PagesData> {
  const [recos, monitored, watch] = await Promise.all([
    sourceRecoApi.list('recomendadas', signal).then((x) => x.items).catch(() => []),
    sourceRecoApi.monitored('all', signal).then((x) => x.items).catch(() => [] as SourceDto[]),
    pre ? Promise.resolve(pre) : loadCompetitorWatch(signal).then((x) => x.watch),
  ]);
  const comps: CompetitorSuggestions[] = watch.map((w) => ({ competitorName: w.competitorName, battlecardId: w.battlecardId, suggestions: w.monitoring.suggestions }));
  const urls = monitored.filter((s) => !s.archivedAt).flatMap((s) => [s.url, s.feedUrl]).filter((u): u is string => !!u);
  return { all: consolidatePages(recos, comps, urls), monitored };
}

/** Uma jornada de objetivo (catálogo + instância viva), sem ler as outras. */
export async function loadObjectiveJourney(key: ObjectiveKey, journeysOn: boolean, signal?: AbortSignal) {
  const all = await loadJourneyObjectives(journeysOn, signal, key);
  return all[key] ?? { catalog: null, journey: null };
}

/** Frase principal da fase atual (consultor), com números reais. */
export function phaseHeadline(key: ObjectiveKey, phase: MacroPhaseView | undefined, f: { competitors?: CompetitorFacts | null; pages?: number }): string {
  if (!phase) return 'Escolha por onde começar.';
  const c = f.competitors;
  if (key === 'concorrentes') {
    switch (phase.key) {
      case 'encontrar': return c && c.items.length ? `Você já tem ${plural(c.items.length, 'empresa', 'empresas')} na lista. Falta conferir quem é quem.` : 'Comece contando quem disputa seus clientes.';
      case 'confirmar': return c && c.toClassify.length ? `Você já conhece seus principais concorrentes. Agora falta decidir o papel de ${countWord(c.toClassify.length, 'empresa', 'empresas')}.` : 'Falta confirmar o papel das empresas encontradas.';
      case 'conhecer': return 'Seus concorrentes estão definidos. Agora é entender como cada um compete.';
      case 'comparar': return 'Você já conhece cada concorrente. Agora é ver onde ganha e onde perde.';
      case 'acompanhar': return f.pages ? `Encontramos ${countWord(f.pages, 'página que vale', 'páginas que vale')} acompanhar.` : 'Agora é acompanhar o que muda e responder a tempo.';
    }
  }
  if (key === 'ofertas') {
    switch (phase.key) {
      case 'mapear': return 'Comece reunindo suas ofertas e para quem cada uma serve.';
      case 'cobrir': return 'Suas ofertas estão mapeadas. Agora é ver onde há lacunas ou sobreposição.';
      case 'comparar': return 'Agora é comparar suas ofertas com concorrentes, substitutos e preços.';
      case 'priorizar': return 'Você já tem a leitura do portfólio. Falta decidir o que priorizar.';
      case 'decidir': return 'Falta registrar a decisão, quem cuida dela e até quando.';
    }
  }
  return phase.question;
}

/** O que a fase atual libera (consequência), ou o que acontece depois da última. */
export function phaseConsequence(phases: readonly MacroPhaseView[], current: MacroPhaseView | undefined): string | null {
  if (!current) return null;
  const i = phases.indexOf(current);
  const next = phases[i + 1];
  if (!next) return 'Depois disso, o InteliMove acompanha as mudanças e avisa o que pede resposta.';
  return current.state === 'AGUARDANDO_DECISAO' ? `Essa decisão libera a fase ${next.name}.` : `Concluir esta fase libera a fase ${next.name}.`;
}

export const objectiveHref = (key: ObjectiveKey, phase?: string | null) => `/objetivos/${key}${phase ? `?fase=${encodeURIComponent(phase)}` : ''}`;
export { objectiveOf };
